import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { ArrowUpDown, Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

export default function MPGReport() {
  const [loads, setLoads] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [assets, setAssets] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('MPG');
  const [sortDir, setSortDir] = useState(-1);
  const company = useCompanyInfo();

  useEffect(() => {
    Promise.all([
      client.entities.Load.list('-delivery_date', 500),
      client.entities.Expense.filter({ category: 'Fuel' }, '-date', 1000),
      client.entities.Asset.filter({ asset_type: 'Truck' }, 'unit_number', 200),
      client.entities.DriverProfile.list('full_name', 100),
    ]).then(([l, e, a, d]) => { setLoads(l); setExpenses(e); setAssets(a); setDrivers(d); setLoading(false); });
  }, []);

  const filteredLoads = loads.filter(l => inRange(l.delivery_date || l.pickup_date, start, end) && l.assigned_driver_email);
  const filteredExpenses = expenses.filter(e => inRange(e.date, start, end) && e.driver_email && e.gallons);

  const driverEmails = [...new Set([...filteredLoads.map(l => l.assigned_driver_email), ...filteredExpenses.map(e => e.driver_email)])];

  let rows = driverEmails.map(email => {
    const driverLoads = filteredLoads.filter(l => l.assigned_driver_email === email);
    const miles = driverLoads.reduce((s, l) => s + (l.miles || 0), 0);
    const gallons = filteredExpenses.filter(e => e.driver_email === email).reduce((s, e) => s + (parseFloat(e.gallons) || 0), 0);
    const driver = drivers.find(d => d.user_email === email);
    // Loads are fetched newest-delivery-first, so the first one carrying a
    // truck_unit snapshot is this driver's most recent unit -- a better
    // label than "whichever truck they're CURRENTLY on," which can be
    // stale by report time if they've since been reassigned.
    const unit = driverLoads.find(l => l.truck_unit)?.truck_unit || assets.find(a => a.assigned_driver_email === email)?.unit_number;
    return {
      Driver: driver?.full_name || email,
      'Unit #': unit || '—',
      Miles: Math.round(miles),
      Gallons: gallons,
      MPG: gallons > 0 ? miles / gallons : 0,
      email,
    };
  }).filter(r => r.Miles > 0 || r.Gallons > 0);

  rows.sort((a, b) => sortDir * ((b[sortKey] ?? 0) - (a[sortKey] ?? 0)));

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d * -1);
    else { setSortKey(key); setSortDir(-1); }
  };

  const fleetMPG = rows.reduce((s, r) => s + r.Gallons, 0) > 0
    ? rows.reduce((s, r) => s + r.Miles, 0) / rows.reduce((s, r) => s + r.Gallons, 0)
    : 0;

  const csvRows = rows.map(r => ({ Driver: r.Driver, 'Unit #': r['Unit #'], Miles: r.Miles, Gallons: r.Gallons.toFixed(3), MPG: r.MPG.toFixed(2) }));

  const handlePrint = () => {
    const cols = ['Driver', 'Unit #', 'Miles', 'Gallons', 'MPG'];
    const dataRows = rows.map(r => [
      { value: r.Driver, class: 'bold' }, r['Unit #'], r.Miles.toLocaleString(), r.Gallons.toFixed(3),
      { value: r.MPG > 0 ? r.MPG.toFixed(2) : '—', class: 'gold' },
    ]);
    const cards = [
      { label: 'Drivers', value: rows.length, color: 'gold' },
      { label: 'Fleet Avg MPG', value: fleetMPG > 0 ? fleetMPG.toFixed(2) : '—', color: 'positive' },
      { label: 'Total Miles', value: rows.reduce((s, r) => s + r.Miles, 0).toLocaleString(), color: '' },
      { label: 'Total Gallons', value: rows.reduce((s, r) => s + r.Gallons, 0).toLocaleString('en-US', { maximumFractionDigits: 1 }), color: '' },
    ];
    const html = generatePrintHTML('Miles Per Gallon Report', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, null, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(csvRows, 'mpg-report.csv')} exportDisabled={loading || rows.length === 0} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: 'Fleet Avg MPG', value: fleetMPG > 0 ? fleetMPG.toFixed(2) : '—', color: 'text-primary' },
          { label: 'Total Miles', value: rows.reduce((s, r) => s + r.Miles, 0).toLocaleString(), color: 'text-foreground' },
          { label: 'Total Gallons', value: rows.reduce((s, r) => s + r.Gallons, 0).toLocaleString('en-US', { maximumFractionDigits: 1 }), color: 'text-foreground' },
        ].map(i => (
          <div key={i.label} className="glass-card p-4 text-center">
            <p className={`text-lg font-space font-bold ${i.color}`}>{i.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{i.label}</p>
          </div>
        ))}
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Driver', 'Unit #', 'Miles', 'Gallons', 'MPG'].map(h => (
                <th key={h} onClick={() => toggleSort(h)} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap cursor-pointer hover:text-foreground select-none">
                  <span className="flex items-center gap-1">{h} <ArrowUpDown className="w-3 h-3 opacity-50" /></span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(r => (
              <tr key={r.email} className="hover:bg-white/3">
                <td className="px-3 py-2.5 font-semibold text-foreground">{r.Driver}</td>
                <td className="px-3 py-2.5 text-primary">{r['Unit #']}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r.Miles.toLocaleString()}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r.Gallons.toFixed(3)}</td>
                <td className="px-3 py-2.5 font-bold text-primary">{r.MPG > 0 ? r.MPG.toFixed(2) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No mileage or fuel data for this period.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
      <p className="text-xs text-muted-foreground">MPG = total loaded miles ÷ total fuel gallons logged per driver in the selected period. Requires gallons entered on Fuel expense records.</p>
    </div>
  );
}
